import React from 'react';
import {FloatingPortal} from '@floating-ui/react';
import {SpringProvider} from '../SpringProvider';
import {useSize} from '@packages/utilities/styled';

export type PortalProps = Omit<
  React.ComponentProps<typeof FloatingPortal>,
  'preserveTabOrder'
>;

/**
 * # Portal
 *
 * Portal renders a component to another part of the DOM, regardless of where
 * the component originally comes from. By default, the component passed into
 * Portal will render into the root of the page (document.body). It uses React
 * Portals underneath.
 *
 * Portalling is essential for Overlay components like Menu, Modal, Popover, and
 * Tooltip.
 *
 * ## Usage
 *
 * ```jsx
 * import {Portal} from '@packages/systems/spring';
 *
 * const Example = () => {
 *   const [isOpen, setIsOpen] = React.useState(false);
 *   return (
 *     <>
 *       <button onClick={toggleOpen}>Toggle Open</button>
 *       {isOpen ? (
 *         <Portal>
 *           <div>
 *             Hello Portal!
 *           </div>
 *         </Portal>
 *       ) : null}
 *     </>
 *   );
 * };
 * ```
 *
 * @component
 */
export const Portal = ({children, id, root}: PortalProps) => {
  const size = useSize();
  return (
    <FloatingPortal id={id} preserveTabOrder={false} root={root}>
      <SpringProvider shouldUseToastProvider={false} size={size}>
        {children}
      </SpringProvider>
    </FloatingPortal>
  );
};

Portal.displayName = 'Portal';

export default Portal;
